"use client"

import { useState, useEffect, useRef } from "react"
import { Calendar, MapPin, Clock, User, Tag, IndianRupee, Mail, Phone, Globe, X, ZoomIn, ArrowLeft } from "lucide-react"
import axios from "axios"
import { toast } from "react-hot-toast"
import { CircleLoader, ClipLoader } from "react-spinners"
import { motion, AnimatePresence } from "framer-motion"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { initializePayment } from "../utils/paymentUtils"
import { PaymentType } from "../utils/paymentTypes"
import { activateEvent } from "../utils/api"
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from "react-icons/fa"
import DOMPurify from "dompurify"

const enterpriseStyles = {
  shadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  shadowLg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  shadowXl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
}

function loadPhonePeScript(src = "https://mercury.phonepe.com/web/bundle/checkout.js") {
  return new Promise((resolve, reject) => {
    if (window.PhonePeCheckout) return resolve()
    const script = document.createElement("script")
    script.src = src
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Failed to load PhonePe Checkout script"))
    document.body.appendChild(script)
  })
}

export default function EventDetail() {
  const [event, setEvent] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showImageZoom, setShowImageZoom] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    userType: "individual",
    founderId: "",
    startupName: "",
    industry: "",
    startupLocation: "",
  })
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [applying, setApplying] = useState(false)
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    founderId: "",
    startupName: "",
    industry: "",
    startupLocation: "",
  })
  const { id } = useParams()
  const navigate = useNavigate()
  const formRef = useRef(null)

  useEffect(() => {
    async function fetchEvent() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/events/${id}`)
        console.log(res.data)
        setEvent(res.data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    fetchEvent()
    loadPhonePeScript().catch(console.error)
  }, [id])

  const validateField = (name, value) => {
    let error = ""

    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required"
        else if (value.length < 3) error = "Name must be at least 3 characters"
        else if (!/^[a-zA-Z\s]*$/.test(value)) error = "Name can only contain letters and spaces"
        break
      case "email":
        if (!value.trim()) error = "Email is required"
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Invalid email format"
        break
      case "phone":
        if (!value.trim()) error = "Phone number is required"
        else if (!/^[0-9]{10}$/.test(value)) error = "Phone must be 10 digits"
        break
      case "founderId":
        if (form.userType === "founder" && !isVerified && value.trim()) {
          error = "Please verify your founder ID"
        }
        break
      case "startupName":
        if (form.userType === "founder" && !value.trim()) error = "Startup name is required"
        break
      case "industry":
        if (form.userType === "founder" && !value.trim()) error = "Industry is required"
        break
      case "startupLocation":
        if (form.userType === "founder" && !value.trim()) error = "Location is required"
        break
      default:
        break
    }

    return error
  }

  const validateForm = () => {
    const newErrors = {};

    // 1. Validate dynamic fields
    event?.fields?.forEach((field) => {
      const key = field.label.toLowerCase().replace(/\s+/g, "_");
      if (field.required && (!form[key] || !form[key].trim())) {
        newErrors[key] = `${field.label} is required`;
      }
    });

    // 2. Validate extra fields for Paid events if not in dynamic fields
    if (event.type?.[0]?.toLowerCase() === "paid") {
      const requiredFields = ["name", "email", "phone"];
      requiredFields.forEach((field) => {
        const existsInFields = event.fields.some(
          (f) => f.label.toLowerCase() === field
        );
        if (!existsInFields && (!form[field] || !form[field].trim())) {
          newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        }
      });
    }

    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };


  const handleChange = (e) => {
    const { name, value } = e.target

    // Prevent non-numeric input for phone field
    if (name === "phone" && value && !/^[0-9]*$/.test(value)) {
      return
    }

    // Prevent non-alphabetic input for name field
    if (name === "name" && value && !/^[a-zA-Z\s]*$/.test(value)) {
      return
    }

    setForm((f) => ({ ...f, [name]: value }))

    // Validate the field when it changes
    if (name in errors) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }))
    }

    // Reset verification if founderId changes
    if (name === "founderId" && isVerified) {
      setIsVerified(false)
    }
  }

  const handleUserTypeChange = (type) => {
    setForm((f) => ({
      ...f,
      userType: type,
      // Clear startup fields when switching to individual
      ...(type === "individual"
        ? {
          founderId: "",
          startupName: "",
          industry: "",
          startupLocation: "",
          isVerified: false,
        }
        : {}),
    }))
    setIsVerified(false)
  }

  const handleVerify = async () => {
    if (!form.founderId) {
      toast.error("Please enter a Founder ID to verify")
      return
    }

    try {
      setVerifyLoading(true)

      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/api/founder-kyc/${form.founderId}`)
      console.log("🔍 verify response:", resp.data)

      const { success, data } = resp.data
      if (!success) {
        throw new Error(resp.data.message || "Verification failed")
      }

      const { founderKyc, startup } = data
      if (!founderKyc?.founderId) {
        throw new Error("Founder not found")
      }

      setForm((f) => ({
        ...f,
        founderId: founderKyc.founderId,
        name: founderKyc.founderName || f.name,
        email: founderKyc.email || f.email,
        phone: founderKyc.phoneNumber || f.phone,
        startupName: startup?.startupName || f.startupName,
        industry: startup?.industry || f.industry,
        startupLocation: startup?.location || founderKyc.address || f.startupLocation,
      }))

      setIsVerified(true)
      toast.success("Founder verified successfully!")
    } catch (err) {
      console.error("Verify error:", err)
      toast.error(err.response?.data?.message || err.message || "Verification failed")
      setIsVerified(false)
    } finally {
      setVerifyLoading(false)
    }
  }

  const handleOpenRegister = () => {
    const token = localStorage.getItem("Startuptoken");
    setShowModal(true);
    setApplying(true);
    setTimeout(() => setApplying(false), 500);
    // if (!token) {
    //   toast.error("This event is only for Tactos Community Members please Login/register to continue");
    //   navigate("/login", { state: { from: location.pathname } });
    //   return;
    // }

  }

  const handlePayButtonClick = () => {
    const token = localStorage.getItem("Startuptoken")
    if (!token) {
      toast.error("Please log in to continue")
      setShowModal(false)
      setProcessing(false)
      navigate("/login", { state: { from: location.pathname } })
      return
    }
    // logged in → submit the form programmatically
    if (formRef.current) formRef.current.requestSubmit()
  }

  const completeEventRegistration = async (transactionId = null) => {

    try {
      const token = localStorage.getItem('Startuptoken');
      if (token === "DEMO_TOKEN") {
        token = null;
      }

      // Restore registration data saved before payment
      const savedData = JSON.parse(sessionStorage.getItem("event_registration")) || {};

      // 🧩 Combine restored + current form safely
      const basePayload = {
        name: form?.name || savedData.name,
        email: form?.email || savedData.email,
        phone: form?.phone || savedData.phone,
        userType: form?.userType || savedData.userType || "individual",
        founderId: form?.founderId || savedData.founderId || null,
        startupName: form?.startupName || savedData.startupName || null,
        industry: form?.industry || savedData.industry || null,
        startupLocation: form?.startupLocation || savedData.startupLocation || null,
        eventId: event?._id || savedData.eventId,
        eventName: event?.name || savedData.eventName,
        eventType: event?.type || savedData.eventType,
        paymentStatus: transactionId ? "Paid" : "Unpaid",
        transactionId: transactionId || null,
      };

      // 🧠 Extract dynamic fields from event
      const eventFields = event?.fields || savedData.fields || [];
      const dynamicFieldData = {};

      // Loop through dynamic fields and get user responses from `form`
      eventFields.forEach((field) => {
        const fieldKey = field.label; // using label as key
        dynamicFieldData[fieldKey] = form?.[fieldKey] || savedData[fieldKey] || "";
      });

      // Combine everything into final payload
      const payload = {
        ...basePayload,
        fields: eventFields.length > 0 ? eventFields.map(f => ({
          label: f.label,
          type: f.type,
          value: dynamicFieldData[f.label] || "",
          required: f.required || false
        })) : [],
      };

      // Include auth headers only if token exists
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      // 🔥 Send request
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/eventregister`,
        payload,
        config
      );

      // ✅ Check success
      if (res?.status === 201 && res?.data?.success) {
        await activateEvent(res.data.eventData.eventId, res.data.registrationId);
        toast.success("Registered successfully!");
        sessionStorage.removeItem("event_registration");
        setShowModal(false);
      } else {
        throw new Error(res?.data?.message || "Failed to complete registration");
      }

      return res.data;

    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.message || "Registration failed");
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    setPayLoading(true)
    e.preventDefault();
    console.log("selectedEvent", event.type[0].toLowerCase())
    const token = localStorage.getItem('Startuptoken');
    const eventType = event?.type?.[0]?.toLowerCase(); // "paid" or "free"
    const eventMode = event?.eventMode;
    // if (selectedEvent?.type?.toLowerCase() === "paid" && !token) {
    //   toast.error("This event is only for Tactos Community Members please Login/register to continue");
    //   setProcessing(false);
    //   navigate("/login", { state: { from: location.pathname } });
    //   return;
    // }


    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (eventMode === "Registered" && !token) {
      toast.error("This event is for registered users only. Please login.");
      setProcessing(false);
      navigate("/login", { state: { from: location.pathname } });
      return;
    }





    try {
      // Store registration data in session storage
      sessionStorage.setItem(
        `event_registration`,
        JSON.stringify({
          ...form,
          eventId: event._id,
          eventName: event.name,
          eventType: event.type,
          eventMode: event.eventMode
        })
      );

      const isPaid = eventType === "paid"

      console.log("type", form.userType)

      if (isPaid) {
        const token = localStorage.getItem('Startuptoken');
        // if (!token) {
        //   toast.error("Please log in to continue");
        //   setProcessing(false);
        //   return;
        // }

        // Convert amount to number and validate
        if (isPaid) {
          const amount = parseFloat(event.amount);
          if (isNaN(amount) || amount <= 0) {
            throw new Error("Invalid event price");
          }
          // Initialize payment
          const paymentResult = await initializePayment({
            type: PaymentType.EVENT,
            amount,
            token,
            returnUrl: `${window.location.origin}/payment-callback`,
          });

          toast.loading("Redirecting to payment gateway...", { id: "payment" });
        }
      } else {
        await completeEventRegistration();
        toast.success("Registration successful!");
        setShowModal(false);

      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Submission failed');
      setProcessing(false);
    } finally {
      setPayLoading(false)
    }
  }

  const [ticketPurchased, setTicketPurchased] = useState(false);

  const handleBuyTicket = () => {
    setTicketPurchased(true);
    setTimeout(() => setTicketPurchased(false), 2000);
  };

  const dateObj = new Date(event?.date);

  const dayMonth = dateObj.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  }); // → "29 Nov"

  const year = dateObj.getFullYear();
  function formatDescription(text) {
    return text
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br />")
  }




  const leftRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState(null);

  useEffect(() => {
    const updateHeight = () => {
      if (leftRef.current) {
        setLeftHeight(leftRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [event]);



  if (loading) {
    return (
      <div className="flex items-center justify-center h-50">
        <img
          src="/tactos.f012ee2d7d02548b905c.png"
          alt="Loading"
          className="w-12 h-12 object-contain animate-pulse"
        />
      </div>

    )
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div
          className="text-center bg-slate-50 p-12 rounded-3xl border border-slate-200 max-w-md"
          style={{ boxShadow: enterpriseStyles.shadowXl }}
        >
          <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ textWrap: "balance" }}>
            Event Not Found
          </h2>
          <p className="text-slate-600 mb-8 text-lg">The event you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-4 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 transition-all duration-200 font-semibold text-lg transform hover:-translate-y-0.5"
            style={{ boxShadow: enterpriseStyles.shadowLg }}
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <div className="">
        <div className="container mx-auto px-6 py-8">
          {/* <button
            onClick={() => navigate(-1)}
            className="mb-8 text-slate-600 hover:text-slate-900 flex items-center gap-3 font-medium transition-colors group text-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Events
          </button> */}

          <div className="max-w-6xl mx-auto">
            <div className="max-w-6xl mx-auto space-y-8">
              {/* Conference Cards Container */}
              <div className="grid md:grid-cols-[1.8fr_1fr] gap-6 items-start">
                {/* LEFT CARD */}
                <div ref={leftRef} className="bg-gradient-to-br from-slate-50 to-slate-200 rounded-3xl p-5 text-white shadow-lg hover:shadow-xl transition-shadow max-h-[480px] flex flex-col order-2 md:order-1">
                  <div className="flex justify-between items-start mb-3">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-black">{dayMonth}, {year}</p>
                      {/* <p className="text-sm font-medium text-black"></p> */}
                    </div>
                    {event.time && <p className="text-sm font-medium text-black">{event.time || "NA"}</p>}
                  </div>

                  <h2 className="text-black text-2xl font-bold leading-tight">
                    {event.name}
                  </h2>

                  <p className="text-black-100 leading-relaxed ">
                    <div className=" rounded-3xl p-3 transition-shadow">
                      <h4 className="font-bold text-sm text-neutral-900 mt-1 mb-3 underline">Event Details</h4>
                      <div className="space-y-4">
                        {(event?.location || event?.address) && (
                          <div>
                            <p className="text-xs font-semibold text-black ">Location</p>
                            <p className="text-black text-sm">
                              {event?.address || event?.location || "NA"}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-xs font-semibold text-black ">Date</p>
                          <p className="text-black text-sm">{dayMonth}, {year}</p>
                        </div>
                        {event.link && event.link.includes("http") && (
                          <div>
                            <p className="text-xs font-semibold text-black">Link</p>
                            <p className="text-black text-sm">
                              <a className="underline text-blue-600" href={event.link} target="_blank">
                                {event.link}
                              </a>
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-sm font-semibold text-black">Price</p>
                          <p className="text-black text-sm">{event.type[0] == "Paid" ? `₹ ${event?.amount}` : "Free"}</p>
                        </div>

                      </div>
                    </div>
                  </p>

                  {event?.status != "Completed" && (
                    <button
                      onClick={handleOpenRegister}
                      className="relative px-4 py-2 bg-black text-white font-semibold rounded-2xl hover:bg-black/80 transition-all duration-300 transform hover:-translate-y-1 text-lg overflow-hidden group"
                      style={{ boxShadow: enterpriseStyles.shadowLg }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                      <div className="relative z-10 flex items-center justify-center gap-3 w-full">
                        {applying ? (
                          <ClipLoader color="currentColor" size={20} />
                        ) : event?.type?.[0]?.toLowerCase() === "paid" ? (
                          <>
                            {`Register for ₹${event.amount}`}
                          </>
                        ) : (
                          "Register Now"
                        )}
                      </div>
                    </button>
                  )}

                </div>

                {/* RIGHT CARD — SAME HEIGHT */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-200 rounded-3xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden flex order-1 md:order-2"
                  style={{ height: leftHeight ? `${leftHeight}px` : "auto" }}>
                  <div
                    className="relative w-full h-auto bg-gradient-to-br from-slate-50 to-slate-200  rounded-2xl cursor-pointer group overflow-hidden"
                    style={{ boxShadow: enterpriseStyles.shadowLg }}
                    onClick={() => setShowImageZoom(true)}
                  >
                    <img
                      src={event.logo || 'https://via.placeholder.com/150'}
                      alt="Event Logo"
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </div>
                </div>
              </div>






              {/* About Section */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-200 rounded-3xl p-8 md:p-12 shadow-lg">

                <div className="prose prose-slate prose-lg max-w-none">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(event.description),
                    }}
                  />

                </div>
              </div>




            </div>

          </div>
        </div>
      </div>

      <AnimatePresence>
        {showImageZoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setShowImageZoom(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={event.logo || "https://via.placeholder.com/150"}
                alt="Event Logo"
                className="max-w-full max-h-full object-contain rounded-3xl"
                style={{ boxShadow: enterpriseStyles.shadowXl }}
              />
              <button
                onClick={() => setShowImageZoom(false)}
                className="absolute top-6 right-6 p-3 bg-white/10 backdrop-blur-sm rounded-2xl text-white hover:bg-white/20 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => {
              setShowModal(false)
              setIsVerified(false)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl p-8 w-full max-w-lg mx-auto relative border border-slate-200"
              style={{ boxShadow: enterpriseStyles.shadowXl }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setShowModal(false)
                  setIsVerified(false)
                }}
                className="absolute top-6 right-6 p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="overflow-y-auto pr-2">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-slate-900 mb-3" style={{ textWrap: "balance" }}>
                    {event.type?.[0]?.toLowerCase() === "paid" ? "Complete Registration" : "Register for Event"}
                  </h3>
                  <p className="text-slate-600 text-lg">Fill in your details to register</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 overflow-auto">
                  {/* User type toggle */}
                  <div className="flex items-center justify-center mb-8">
                    <button
                      type="button"
                      onClick={() => handleUserTypeChange("individual")}
                      className={`px-8 py-4 text-base font-semibold rounded-2xl transition-all ${form.userType === "individual"
                        ? "bg-black text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      style={form.userType === "individual" ? { boxShadow: enterpriseStyles.shadowLg } : {}}
                    >
                      Applicant
                    </button>
                  </div>

                  {/* Form fields with enterprise styling */}
                  <div className="space-y-6">
                    {event.type[0] === "Paid" &&
                      !event.fields.some(
                        (f) => f.label.toLowerCase() === "email"
                      ) && (
                        <div className="mb-3">
                          <input
                            type="text"
                            name="name"
                            placeholder="Name *"
                            required
                            value={form.name || ""}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className={`w-full p-5 text-base border-2 ${errors.phone ? "border-red-500 bg-red-50" : "border-slate-200 bg-slate-50"
                              } rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-500`}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                          )}
                        </div>
                      )}
                    {event.type[0] === "Paid" &&
                      !event.fields.some(
                        (f) => f.label.toLowerCase() === "email"
                      ) && (
                        <div className="mb-3">
                          <input
                            type="email"
                            name="email"
                            placeholder="Email *"
                            required
                            value={form.email || ""}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className={`w-full p-5 text-base border-2 ${errors.phone ? "border-red-500 bg-red-50" : "border-slate-200 bg-slate-50"
                              } rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-500`}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                          )}
                        </div>
                      )}

                    {event.type[0] === "Paid" &&
                      !event.fields.some(
                        (f) => f.label.toLowerCase() === "phone"
                      ) && (
                        <div className="mb-3">
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Phone *"
                            required
                            value={form.phone || ""}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className={`w-full p-5 text-base border-2 ${errors.phone ? "border-red-500 bg-red-50" : "border-slate-200 bg-slate-50"
                              } rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-500`}
                          />
                          {errors.phone && (
                            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                          )}
                        </div>
                      )}

                    {event?.fields?.length === 0 && event?.type?.[0] !== "paid" && (
                      <div className="text-center py-6">
                        <p className="text-gray-700 font-medium">
                          No additional details are required for this event.
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          You can proceed and apply directly.
                        </p>
                      </div>
                    )}



                  </div>

                  {/* Startup founder fields */}
                  {form.userType === "founder" && (
                    <div className="space-y-4">
                      <div className="flex items-end space-x-2">
                        <div className="flex-1 flex flex-col">
                          <label htmlFor="founderId" className="text-sm text-slate-700 font-medium mb-1">
                            Founder ID
                          </label>
                          <input
                            id="founderId"
                            name="founderId"
                            type="text"
                            placeholder="Enter Founder ID (optional)"
                            value={form.founderId}
                            onChange={handleChange}
                            className={`w-full p-2 sm:p-3 text-sm sm:text-base border ${errors.founderId ? "border-red-500" : "border-slate-200"} rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400`}
                          />
                          {errors.founderId && <p className="text-red-500 text-xs mt-1">{errors.founderId}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={handleVerify}
                          disabled={verifyLoading || !form.founderId || isVerified}
                          className="px-3 py-1 sm:px-4 sm:py-2 mb-1 text-sm sm:text-base bg-emerald-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                        >
                          {verifyLoading ? "Verifying..." : isVerified ? "Verified" : "Verify"}
                        </button>
                      </div>

                      {isVerified && (
                        <div className="bg-green-50 p-2 sm:p-3 rounded-lg text-green-700 text-xs sm:text-sm">
                          Founder verified successfully
                        </div>
                      )}

                      <div>
                        <input
                          name="startupName"
                          type="text"
                          placeholder="Startup Name *"
                          value={form.startupName}
                          onChange={handleChange}
                          required
                          className={`w-full p-2 sm:p-3 text-sm sm:text-base border ${errors.startupName ? "border-red-500" : "border-slate-200"} rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400`}
                        />
                        {errors.startupName && <p className="text-red-500 text-xs mt-1">{errors.startupName}</p>}
                      </div>
                      <div>
                        <input
                          name="industry"
                          type="text"
                          placeholder="Industry *"
                          value={form.industry}
                          onChange={handleChange}
                          required
                          className={`w-full p-2 sm:p-3 text-sm sm:text-base border ${errors.industry ? "border-red-500" : "border-slate-200"} rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400`}
                        />
                        {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                      </div>
                      <div>
                        <input
                          name="startupLocation"
                          type="text"
                          placeholder="Startup Location *"
                          value={form.startupLocation}
                          onChange={handleChange}
                          required
                          className={`w-full p-2 sm:p-3 text-sm sm:text-base border ${errors.startupLocation ? "border-red-500" : "border-slate-200"} rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400`}
                        />
                        {errors.startupLocation && (
                          <p className="text-red-500 text-xs mt-1">{errors.startupLocation}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enhanced submit button */}
                  {event.type?.[0]?.toLowerCase() === "paid" && (
                    <div className="bg-emerald-50 p-6 rounded-2xl text-center mb-6 border border-emerald-200">
                      <p className="text-emerald-700 font-semibold text-lg">Registration Fee: ₹{event.amount}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={processing}
                    className="w-full py-5 text-lg bg-black text-white font-semibold rounded-2xl hover:bg-black/80 transition-all duration-300 disabled:opacity-70 flex items-center justify-center transform hover:-translate-y-0.5 relative overflow-hidden group"
                    style={{ boxShadow: enterpriseStyles.shadowLg }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                    <div className="relative z-10 flex items-center gap-3">
                      {processing ? (
                        <ClipLoader color="currentColor" size={20} />
                      ) : event?.type?.[0]?.toLowerCase() === "paid" ? (
                        payLoading ? (
                          <>
                            <ClipLoader color="currentColor" size={20} />
                            Loading...
                          </>
                        ) : (
                          <>
                            <IndianRupee className="w-5 h-5" />
                            {`Pay ${event.amount}`}
                          </>
                        )
                      ) : (
                        "Complete Registration"
                      )}
                    </div>

                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
